import { useAtomValue, useSetAtom } from 'jotai';
import { atom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation,useNavigate, useParams } from 'react-router-dom';

import type { LeadsTableTab } from '@ekhodealer/ekho-common';

import { paths } from '@config/paths';

import { LeadDetailsTab } from '../atoms/leadDetailsDrawerAtoms';
import { currentPageAtom, itemsPerPageAtom } from '../atoms/leadsAtoms';

// Atom to store current leads list for navigation
export const currentLeadsListAtom = atom<string[]>([]);

/**
 * Custom hook for lead-related navigation
 * Encapsulates the logic for navigating to lead details and closing the drawer
 *
 * @example
 * ```tsx
 * const { openLeadDetails, closeLeadDetails, navigateToPreviousLead, navigateToNextLead } = useLeadNavigation();
 *
 * // Open lead details to a specific tab
 * openLeadDetails('lead-123', LeadDetailsTab.OVERVIEW);
 *
 * // Navigate to adjacent leads
 * navigateToPreviousLead('lead-123');
 * navigateToNextLead('lead-123');
 *
 * // Close the drawer
 * closeLeadDetails();
 * ```
 */
export function useLeadNavigation() {
	const navigate = useNavigate();
	const location = useLocation();
	const { servicesProviderId, tabId, leadId } = useParams<{
		servicesProviderId: string;
		tabId: string;
		leadId?: string;
	}>();

	// Get current leads list and pagination state
	const currentLeadsList = useAtomValue(currentLeadsListAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Track pending navigation after page change
	const pendingNavigationRef = useRef<{
		action: 'next' | 'prev';
		targetTab: LeadDetailsTab;
	} | null>(null);

	/**
	 * Navigate to lead details with a specific tab
	 * @param leadId - The ID of the lead to open
	 * @param detailsTab - The tab to open (defaults to OVERVIEW)
	 */
	const openLeadDetails = useCallback(
		(leadId: string, detailsTab: LeadDetailsTab = LeadDetailsTab.OVERVIEW) => {
			if (!servicesProviderId || !tabId) {
				console.warn(
					'Cannot navigate to lead details: missing servicesProviderId or tabId',
				);
				return;
			}

			// Preserve query parameters (filters, pagination, etc.) when opening details
			const path = paths.leadDetails.getHref(
				servicesProviderId,
				tabId as LeadsTableTab,
				leadId,
				detailsTab,
			);
			navigate(path + location.search);
		},
		[navigate, servicesProviderId, tabId, location.search],
	);

	/**
	 * Close the lead details drawer and return to the leads list
	 */
	const closeLeadDetails = () => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot close lead details: missing servicesProviderId or tabId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when closing details
		const path = paths.leadsTab.getHref(servicesProviderId, tabId as LeadsTableTab);
		navigate(path + location.search);
	};

	/**
	 * Change the active tab in the lead details drawer
	 * @param leadId - The ID of the currently open lead
	 * @param detailsTab - The new tab to switch to
	 */
	const switchLeadDetailsTab = (leadId: string, detailsTab: LeadDetailsTab) => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot switch tab: missing servicesProviderId or tabId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when switching tabs
		const path = paths.leadDetails.getHref(
			servicesProviderId,
			tabId as LeadsTableTab,
			leadId,
			detailsTab,
		);
		navigate(path + location.search);
	};

	/**
	 * Navigate to the previous lead in the list
	 * Handles pagination automatically if needed
	 * @param currentLeadId - The ID of the currently displayed lead
	 * @param detailsTab - The tab to maintain when navigating (defaults to OVERVIEW)
	 */
	const navigateToPreviousLead = (
		currentLeadId: string,
		detailsTab: LeadDetailsTab = LeadDetailsTab.OVERVIEW,
	) => {
		const currentIndex = currentLeadsList.indexOf(currentLeadId);

		if (currentIndex > 0) {
			// Previous lead is on current page
			const prevLeadId = currentLeadsList[currentIndex - 1];
			openLeadDetails(prevLeadId, detailsTab);
		} else if (currentIndex === 0 && currentPage > 1) {
			// Need to go to previous page - go to last item of previous page
			pendingNavigationRef.current = { action: 'prev', targetTab: detailsTab };
			setCurrentPage(currentPage - 1);
		}
		// If at the beginning of first page, do nothing
	};

	/**
	 * Navigate to the next lead in the list
	 * Handles pagination automatically if needed
	 * @param currentLeadId - The ID of the currently displayed lead
	 * @param detailsTab - The tab to maintain when navigating (defaults to OVERVIEW)
	 */
	const navigateToNextLead = (
		currentLeadId: string,
		detailsTab: LeadDetailsTab = LeadDetailsTab.OVERVIEW,
	) => {
		const currentIndex = currentLeadsList.indexOf(currentLeadId);

		if (currentIndex >= 0 && currentIndex < currentLeadsList.length - 1) {
			// Next lead is on current page
			const nextLeadId = currentLeadsList[currentIndex + 1];
			openLeadDetails(nextLeadId, detailsTab);
		} else if (currentIndex === currentLeadsList.length - 1) {
			// Need to go to next page - go to first item of next page
			pendingNavigationRef.current = { action: 'next', targetTab: detailsTab };
			setCurrentPage(currentPage + 1);
		}
		// If at the end of results, do nothing (boundary check should disable button)
	};

	/**
	 * Get navigation state for the current lead
	 * Returns information about whether prev/next navigation is possible
	 */
	const getNavigationState = (currentLeadId: string, totalCount: number) => {
		const currentIndex = currentLeadsList.indexOf(currentLeadId);
		const isFirstOnPage = currentIndex === 0;
		const isLastOnPage = currentIndex === currentLeadsList.length - 1;
		const isFirstPage = currentPage === 1;
		const totalPages = Math.ceil(totalCount / itemsPerPage);
		const isLastPage = currentPage >= totalPages;

		return {
			canGoPrevious: !(isFirstOnPage && isFirstPage),
			canGoNext: !(isLastOnPage && isLastPage),
			currentIndex,
			totalInCurrentPage: currentLeadsList.length,
		};
	};

	// Effect to handle navigation after page change
	useEffect(() => {
		if (pendingNavigationRef.current && currentLeadsList.length > 0) {
			const { action, targetTab } = pendingNavigationRef.current;

			if (action === 'next') {
				// Navigate to first lead of new page
				openLeadDetails(currentLeadsList[0], targetTab);
			} else if (action === 'prev') {
				// Navigate to last lead of new page
				openLeadDetails(currentLeadsList[currentLeadsList.length - 1], targetTab);
			}

			// Clear pending navigation
			pendingNavigationRef.current = null;
		}
	}, [currentLeadsList, openLeadDetails]);

	return {
		openLeadDetails,
		closeLeadDetails,
		switchLeadDetailsTab,
		navigateToPreviousLead,
		navigateToNextLead,
		getNavigationState,
		// Expose params in case components need them
		servicesProviderId,
		tabId,
		leadId,
	};
}
